import { Group, Mesh, MeshStandardMaterial, PlaneGeometry, Vector2, Vector3 } from 'three';
import { ActionControllerEventName, ButtonComponent, ParentEvent, ViewerModule, WindowComponent, RoundedPlane } from '@verybigthings/vbt-webvr-framework';
import { BUTTON_PRIMARY_COLOR, BUTTON_PRIMARY_TEXT_COLOR, SLIDER_BORDER_COLOR } from '../styleConfig';
export var SlidingMode;
(function (SlidingMode) {
    SlidingMode["normal"] = "normal";
    SlidingMode["onlyToRight"] = "onlyToRight";
    SlidingMode["onlyToLeft"] = "onlyToLeft";
})(SlidingMode || (SlidingMode = {}));
export class SliderComponent extends ViewerModule {
    constructor(options) {
        super();
        this.slideActive = false;
        this.mode = options.mode || SlidingMode.normal;
        this.magnetOnSides = options.magnetOnSides || 0;
        this.options = options;
        this.mainContainer = new Group();
        this.mainContainer.name = 'Main Container';
        this.width = options.size ? options.size.width : 2;
        this.height = options.size ? options.size.height : 0.2;
        this.uiObject.visible = false;
        this.addObject(this.mainContainer);
        this.init();
    }
    get value() {
        return this._value;
    }
    set value(value) {
        this._value = value;
        this.updateFilledPart();
    }
    init() {
        this.visualGroup = new Group();
        this.visualGroup.name = 'Visual Group';
        this.mainContainer.add(this.visualGroup);
        if (!this.options.applyDisabled) {
            this.addObject(this.applyBtn = new ButtonComponent({
                type: 'flat',
                size: new Vector3(1.5, 1.5, 1),
                sizePx: new Vector2(256, 256),
                bordRadius: 0.03,
                background: { color: BUTTON_PRIMARY_COLOR.getStyle() },
                text: {
                    value: 'Apply'.toUpperCase(),
                    lineHeight: 34,
                    style: { size: '48px', color: BUTTON_PRIMARY_TEXT_COLOR.getStyle(), family: 'Nunito', weight: '800' },
                    margin: { top: 22, right: 45, bottom: 26, left: 45 },
                }
            }));
            this.applyBtn.uiObject.position.x = 1.48;
            this.applyBtn.uiObject.rotateZ(Math.PI / 2);
            this.applyBtn.on(ActionControllerEventName.click, () => this.fire('completed'));
        }
        this.createDrag();
        this.createBackground();
        this.createFilledPart();
        this.updateFilledPart();
    }
    createBackground() {
        this.background = new WindowComponent({
            size: new Vector2(this.width - this.options.unfilledPart.border, this.height - this.options.unfilledPart.border),
            bordRadius: this.options.unfilledPart.radius,
            bordWidth: this.options.unfilledPart.border,
            bordColor: this.options.unfilledPart.borderColor.getHex(),
            background: this.options.unfilledPart.mainColor.getHex(),
        });
        this.addObject(this.background);
    }
    createFilledPart() {
        this.filledPart = new WindowComponent({
            size: new Vector2((this.width - this.options.filledPart.border) * this._value, this.height - this.options.filledPart.border),
            bordRadius: this.options.filledPart.radius,
            bordWidth: this.options.filledPart.border,
            bordColor: this.options.filledPart.borderColor.getHex(),
            background: this.options.filledPart.mainColor.getHex(),
        });
        this.addObject(this.filledPart);
    }
    updateFilledPart() {
        this.filledPart.setSize(new Vector2((this.width - this.options.filledPart.border) * this._value, this.height - this.options.filledPart.border));
        this.filledPart.uiObject.position.set((this.mode === SlidingMode.onlyToRight || this.mode === SlidingMode.normal ? -1 : 1) * 0.5 * (this.width - ((this.width - this.options.filledPart.border) * this._value)), 0, 0.01);
    }
    addPickerBorder() {
        const borderWidth = this.options.filledPart.border || this.options.unfilledPart.border || 0;
        if (borderWidth) {
            const roundedPlaneOptions = {
                width: this.options.picker.options.size.x + borderWidth * 2,
                height: this.options.picker.options.size.y + borderWidth * 2,
                color: SLIDER_BORDER_COLOR.getHex(),
                r1: 0.18,
            };
            const pickerBorder = RoundedPlane.getRoundedPlane(roundedPlaneOptions);
            this.slider.addObject(pickerBorder);
            pickerBorder.position.z = 0.01;
        }
    }
    addBlinkPicker() {
        const borderWidth = this.options.filledPart.border || this.options.unfilledPart.border || 0;
        const roundedPlaneOptions = {
            width: this.options.picker.options.size.x + borderWidth * 2,
            height: this.options.picker.options.size.y + borderWidth * 2,
            color: 0x00ff00,
            r1: 0.15,
        };
        this.blinkPicker = RoundedPlane.getRoundedPlane(roundedPlaneOptions);
        this.blinkPicker.material.transparent = true;
        this.slider.addObject(this.blinkPicker);
        this.blinkPicker.position.z = 0.03;
        this.animProperty(this.blinkPicker.material, 'opacity', 1, 0);
    }
    animProperty(object, property, oldVal, newVal) {
        this.pickerBlinkTween = new TWEEN.Tween({ alpha: oldVal }).to({ alpha: newVal }, 700)
            .onUpdate((alpha) => {
            object[property] = (oldVal > newVal) ? alpha : newVal - alpha;
        })
            .onComplete(() => this.animProperty(object, property, newVal, oldVal))
            .start();
    }
    createDrag() {
        this.dragGroup = new Group();
        this.dragGroup.name = 'Drag Group';
        this.mainContainer.add(this.dragGroup);
        // const sliderStart = -(this.width - sliderWidth) / 2;
        const sliderStart = (this.mode === SlidingMode.normal || this.mode === SlidingMode.onlyToRight) ? -this.width / 2 : this.width / 2;
        this.slider = this.options.picker;
        this.addPickerBorder();
        this.addBlinkPicker();
        // this.slider = new IconButtonComponent();
        this.slider.uiObject.position.set(sliderStart, 0, 0);
        this.addObject(this.slider, null, this.dragGroup);
        // const sliderPlaneWidth = this.width - sliderWidth;
        const slidePlane = new Mesh(new PlaneGeometry(this.width * 2, this.height * 5), new MeshStandardMaterial({
            color: '#333333',
            side: 2,
            visible: false
        }));
        this.dragGroup.add(slidePlane);
        slidePlane.visible = false;
        this.actionController.on(ActionControllerEventName.buttonDown, this.slider.uiObject, () => {
            this.slideActive = true;
            this.controls.enabled = false;
            slidePlane.visible = true;
        });
        this.actionController.on(ActionControllerEventName.buttonUp, null, () => {
            this.slideActive = false;
            this.controls.enabled = true;
            slidePlane.visible = false;
        });
        this.actionController.on(ActionControllerEventName.move, slidePlane, (event) => {
            if (this.slideActive) {
                const point = event.point;
                this.moveEvent(this.uiObject.worldToLocal(point));
            }
        });
        this.on('completed', () => {
            this.slideActive = false;
            // this.controls.enabled = true;
            slidePlane.visible = false;
        });
    }
    moveEvent(point) {
        this.blinkPicker.visible = false;
        let x;
        switch (this.mode) {
            case SlidingMode.normal:
                x = point.x;
                break;
            case SlidingMode.onlyToLeft:
                if (this.slider.uiObject.position.x > point.x)
                    x = point.x;
                else
                    x = this.slider.uiObject.position.x;
                break;
            case SlidingMode.onlyToRight:
                if (this.slider.uiObject.position.x < point.x)
                    x = point.x;
                else
                    x = this.slider.uiObject.position.x;
                break;
        }
        const alphaBeforeMagnet = (x + this.width / 2) / this.width;
        if (this.magnetOnSides && (this.mode === SlidingMode.normal || this.mode === SlidingMode.onlyToLeft)) {
            if (alphaBeforeMagnet < this.magnetOnSides) {
                x = -this.width / 2;
            }
        }
        if (this.magnetOnSides && (this.mode === SlidingMode.normal || this.mode === SlidingMode.onlyToRight)) {
            if ((1 - alphaBeforeMagnet) < this.magnetOnSides) {
                x = this.width / 2;
            }
        }
        if (x !== undefined) {
            this.slider.uiObject.position.x = x;
            this.slider.uiObject.position.z = 0.02;
            let alpha = (x + this.width / 2) / this.width;
            if (this.mode === SlidingMode.onlyToLeft)
                alpha = 1 - alpha;
            this.value = alpha;
            this.fire('change', new ParentEvent('change', alpha));
            if (alpha === 1) {
                this.fire('completed', new ParentEvent('completed', alpha));
            }
        }
    }
    //
    show() {
        this.uiObject.visible = true;
    }
    hide() {
        this.uiObject.visible = false;
    }
}
